/**
 * ORDENADOR — cartões que o aluno põe em ordem.
 *
 * Dois caminhos para o mesmo gesto: arrastar o cartão (mouse ou dedo) e os botões ↑ e
 * ↓ de cada cartão. Os botões não são um enfeite de acessibilidade: num celular,
 * arrastar numa lista que também rola a página é frustrante, e tocar duas vezes num
 * botão é certeiro. Pelo teclado, Tab chega aos botões.
 */

import type { RichText } from '../schema/richtext.ts';
import { renderRichInline } from './richtext.ts';

export interface Ordenador {
  readonly element: HTMLElement;
  /** A sequência atual: índices dos itens, do primeiro ao último. */
  ordem(): number[];
  /** O aluno já mexeu na lista? Sem isso, não há resposta. */
  tocado(): boolean;
  travar(): void;
  /** Revisão: pinta cada cartão como certo ou errado de posição. */
  marcar(certa: readonly number[]): void;
}

export function criarOrdenador(
  itens: readonly RichText[],
  inicial: readonly number[],
  pontas: readonly [string, string],
  bloqueadoDeInicio: boolean,
): Ordenador {
  let bloqueado = bloqueadoDeInicio;
  let mexeu = false;

  const root = document.createElement('div');
  root.className = bloqueado ? 'ordenar is-bloqueado' : 'ordenar';
  const topo = document.createElement('p');
  topo.className = 'ordenar-ponta';
  topo.textContent = `↑ ${pontas[0]}`;
  const lista = document.createElement('ol');
  lista.className = 'ordenar-lista';
  lista.setAttribute('aria-label', `Cartões, do ${pontas[0]} para o ${pontas[1]}`);
  const fim = document.createElement('p');
  fim.className = 'ordenar-ponta';
  fim.textContent = `↓ ${pontas[1]}`;
  root.append(topo, lista, fim);

  const cartoes = new Map<number, HTMLLIElement>();

  const atualizar = (): void => {
    const lis = [...lista.children] as HTMLLIElement[];
    lis.forEach((li, pos) => {
      const texto = li.querySelector('.cartao-corpo')?.textContent ?? '';
      const [sobe, desce] = li.querySelectorAll<HTMLButtonElement>('button');
      if (sobe) {
        sobe.disabled = bloqueado || pos === 0;
        sobe.setAttribute('aria-label', `Subir ${texto} (posição ${pos + 1} de ${lis.length})`);
      }
      if (desce) {
        desce.disabled = bloqueado || pos === lis.length - 1;
        desce.setAttribute('aria-label', `Descer ${texto} (posição ${pos + 1} de ${lis.length})`);
      }
    });
  };

  const mover = (li: HTMLLIElement, delta: -1 | 1): void => {
    if (bloqueado) return;
    if (delta < 0 && li.previousElementSibling) lista.insertBefore(li, li.previousElementSibling);
    else if (delta > 0 && li.nextElementSibling) lista.insertBefore(li.nextElementSibling, li);
    else return;
    mexeu = true;
    atualizar();
  };

  for (const i of inicial) {
    const li = document.createElement('li');
    li.className = 'cartao';
    li.dataset['item'] = String(i);
    const pega = document.createElement('span');
    pega.className = 'cartao-pega';
    pega.setAttribute('aria-hidden', 'true');
    pega.textContent = '⋮⋮';
    const corpo = document.createElement('span');
    corpo.className = 'cartao-corpo';
    corpo.appendChild(renderRichInline(itens[i] ?? []));
    const botoes = document.createElement('span');
    botoes.className = 'cartao-botoes';
    for (const [rotulo, delta] of [
      ['↑', -1],
      ['↓', 1],
    ] as const) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cartao-botao';
      b.textContent = rotulo;
      b.addEventListener('click', () => {
        mover(li, delta);
        // Mover o cartão tira o foco do botão; ele volta — e, se o cartão chegou à
        // ponta e o botão travou, passa ao outro botão do mesmo cartão, em vez de cair
        // no começo da página.
        const outro = b.parentElement?.querySelector<HTMLButtonElement>(`button:not(:disabled)`);
        (b.disabled ? outro : b)?.focus();
      });
      botoes.appendChild(b);
    }
    li.append(pega, corpo, botoes);
    lista.appendChild(li);
    cartoes.set(i, li);

    // Arrastar: o cartão acompanha o ponteiro e troca de lugar ao passar do meio do
    // vizinho. A lista muda ao vivo — não há "fantasma" a soltar num lugar imaginado.
    li.addEventListener('pointerdown', (e) => {
      if (bloqueado || (e.target as HTMLElement).closest('button')) return;
      e.preventDefault();
      li.classList.add('is-arrastando');
      try {
        li.setPointerCapture(e.pointerId);
      } catch {
        // sem captura (ambiente de teste)
      }
    });
    li.addEventListener('pointermove', (e) => {
      if (!li.classList.contains('is-arrastando')) return;
      const antes = li.previousElementSibling as HTMLElement | null;
      const depois = li.nextElementSibling as HTMLElement | null;
      if (antes) {
        const r = antes.getBoundingClientRect();
        if (e.clientY < r.top + r.height / 2) mover(li, -1);
      }
      if (depois) {
        const r = depois.getBoundingClientRect();
        if (e.clientY > r.top + r.height / 2) mover(li, 1);
      }
    });
    const soltar = (): void => li.classList.remove('is-arrastando');
    li.addEventListener('pointerup', soltar);
    li.addEventListener('pointercancel', soltar);
  }
  atualizar();

  return {
    element: root,
    ordem: () => [...lista.children].map((li) => Number((li as HTMLElement).dataset['item'])),
    tocado: () => mexeu,
    travar() {
      bloqueado = true;
      root.classList.add('is-bloqueado');
      atualizar();
    },
    marcar(certa) {
      [...lista.children].forEach((li, pos) => {
        const ok = Number((li as HTMLElement).dataset['item']) === certa[pos];
        li.classList.add(ok ? 'is-ok' : 'is-err');
      });
    },
  };
}
